import { RouteObject, Navigate } from 'react-router-dom'
import { getRouteComponent, routeRegistry } from './routeRegistry'
import { AuthGuard } from '@/components/auth/AuthGuard'

interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
}

/**
 * Generate React Router routes from user_routes (database navigation)
 */
export function generateRoutesFromUserRoutes(
  userRoutes: UserRoute[],
  publicRoutes: RouteObject[] = []
): RouteObject[] {
  const routes: RouteObject[] = [
    // Public routes (always available)
    ...publicRoutes,
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
  ]

  /**
   * Recursively process routes and their children
   */
  function processRoute(route: UserRoute, parentPath: string = '') {
    let fullPath: string
    
    if (route.path.startsWith('/')) {
      // Absolute path - check if it exists in route registry
      // If not, try joining with parentPath (for cases like /users under /user-management)
      const absolutePath = route.path
      const ComponentForAbsolute = getRouteComponent(absolutePath)
      
      if (ComponentForAbsolute) {
        // Absolute path exists in registry (e.g., /shipping-lines/create)
        fullPath = absolutePath
      } else if (parentPath) {
        // Absolute path doesn't exist, try joining with parent
        // (e.g., /users under /user-management becomes /user-management/users)
        const joinedPath = `${parentPath}${absolutePath}`
        const ComponentForJoined = getRouteComponent(joinedPath)
        if (ComponentForJoined) {
          fullPath = joinedPath
        } else {
          // Neither absolute nor joined path exists, use absolute as fallback
          fullPath = absolutePath
        }
      } else {
        // No parent path, use absolute as-is
        fullPath = absolutePath
      }
    } else {
      // Relative path - join with parentPath
      fullPath = parentPath ? `${parentPath}${route.path}` : route.path
    }

    // Get component for this route
    const Component = getRouteComponent(fullPath)
    
    const alreadyRegistered = routes.some((r) => r.path === fullPath)

    if (Component && !alreadyRegistered) {
      routes.push({
        path: fullPath,
        element: (
          <AuthGuard>
            <Component />
          </AuthGuard>
        ),
      })
    }

    // Process children
    // Use fullPath as the parent path for children
    // fullPath is always set (either absolute, joined, or relative path)
    // This ensures children are processed with the correct parent context
    // For example: /user-management (parent) -> /users (child) becomes /user-management/users
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => {
        // Pass fullPath as parent so children can be joined correctly
        // The processRoute function will:
        // 1. Check if child's absolute path exists in registry (e.g., /shipping-lines/create)
        // 2. If not, try joining with parentPath (e.g., /users + /user-management = /user-management/users)
        processRoute(child, fullPath)
      })
    }
  }

  // Process all user routes
  userRoutes.forEach((route) => {
    processRoute(route)
  })

  // Always ensure dashboard route exists (even if user_routes is empty or doesn't include it)
  // Check if dashboard route already exists
  const hasDashboard = routes.some((r) => r.path === '/dashboard')
  if (!hasDashboard) {
    const DashboardComponent = getRouteComponent('/dashboard')
    if (DashboardComponent) {
      routes.push({
        path: '/dashboard',
        element: (
          <AuthGuard>
            <DashboardComponent />
          </AuthGuard>
        ),
      })
    }
  }

  // Register dynamic routes from registry (e.g. /ticket-management/all-tickets/:id/update-status)
  // These are not in user_routes nav but must be available for direct navigation
  Object.keys(routeRegistry).forEach((path) => {
    if (path.includes(':') && !routes.some((r) => r.path === path)) {
      const Component = routeRegistry[path]
      if (Component) {
        routes.push({
          path,
          element: (
            <AuthGuard>
              <Component />
            </AuthGuard>
          ),
        })
      }
    }
  })

  // Register all static routes from registry that are not yet in routes
  // Ensures paths like /ticket-management/all-tickets work even when user_routes omit them
  const publicPaths = ['/login', '/forgot-password', '/reset-password', '/two-factor', '/two-factor/setup', '/csat', '/csat-submit', '/csat/thank-you']
  Object.keys(routeRegistry).forEach((path) => {
    const isPublic = publicPaths.includes(path) || publicPaths.some((p) => path.startsWith(p + '/'))
    if (!path.includes(':') && !isPublic && !routes.some((r) => r.path === path)) {
      const Component = routeRegistry[path]
      if (Component) {
        routes.push({
          path,
          element: (
            <AuthGuard>
              <Component />
            </AuthGuard>
          ),
        })
      }
    }
  })

  // Fallback to dashboard for any unmatched authenticated route
  // Only add catch-all if we have routes (don't redirect during initialization)
  if (routes.length > publicRoutes.length + 1) { // More than just public routes + root redirect
    routes.push({
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    })
  }

  return routes
}

